// src/hooks/useUserInfo.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAllUsers } from '../api/user';
let hasFetchedUsers = false;
let cachedUsers: UserInfo[] = [];
let cachedUserMap: Map<string, UserInfo> = new Map();

interface UserInfo {
  id: string;
  name: string;
  email: string;
  role: string;
  firstName: string;
  lastName: string;
}

export function useUserInfo() {
  const { user } = useAuth();
  const [userMap, setUserMap] = useState<Map<string, UserInfo>>(new Map());
  const [allUsersList, setAllUsersList] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const fetchCalledRef = useRef(false);

  // Current user from JWT
  const currentUser: UserInfo | null = user ? {
    id: user.id || user.sub || '',
    name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email?.split('@')[0] || 'User',
    email: user.email || '',
    role: user.role || 'employee',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
  } : null;

  // Fetch all users from /users/all endpoint - only once
  // const fetchAllUsers = useCallback(async () => {
  //   // Prevent multiple calls
  //   if (fetchCalledRef.current) return;
  //   if (loading) return;
  //   if (initialLoadDone && userMap.size > 0) return;
    
  //   fetchCalledRef.current = true;
  //   setLoading(true);
    
  //   try {
  //     const response = await getAllUsers();
  //     const users = response.users || [];
      
  //     const map = new Map<string, UserInfo>();
  //     const usersList: UserInfo[] = [];
      
  //     users.forEach((usr: any) => {
  //       const userInfo: UserInfo = {
  //         id: usr.id || usr.user_id,
  //         name: usr.name || `${usr.first_name || ''} ${usr.last_name || ''}`.trim() || usr.email?.split('@')[0] || 'User',
  //         email: usr.email,
  //         role: usr.role || 'employee',
  //         firstName: usr.first_name || '',
  //         lastName: usr.last_name || '',
  //       };
        
  //       map.set(userInfo.id, userInfo);
  //       usersList.push(userInfo);
  //     });
      
  //     setUserMap(map);
  //     setAllUsersList(usersList);
  //     setInitialLoadDone(true);
  //     console.log('✅ All users loaded once:', usersList.length);
  //   } catch (error) {
  //     console.error('Failed to fetch users:', error);
  //     fetchCalledRef.current = false; // Reset on error to allow retry
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [loading, initialLoadDone, userMap.size]);

  const fetchAllUsers = useCallback(async () => {
  if (hasFetchedUsers) {
    setAllUsersList(cachedUsers);
    setUserMap(cachedUserMap);
    return;
  }

  if (loading) return;

  setLoading(true);

  try {
    const response = await getAllUsers();
    const users = response.users || [];

    const map = new Map<string, UserInfo>();
    const usersList: UserInfo[] = [];

    users.forEach((usr: any) => {
      const userInfo: UserInfo = {
        id: usr.id || usr.user_id,
        name:
          usr.name ||
          `${usr.first_name || ''} ${usr.last_name || ''}`.trim() ||
          usr.email?.split('@')[0] ||
          'User',
        email: usr.email,
        role: usr.role || 'employee',
        firstName: usr.first_name || '',
        lastName: usr.last_name || '',
      };

      map.set(userInfo.id, userInfo);
      usersList.push(userInfo);
    });

    // ✅ cache set
    cachedUsers = usersList;
    cachedUserMap = map;
    hasFetchedUsers = true;

    setUserMap(map);
    setAllUsersList(usersList);
    setInitialLoadDone(true);

    console.log('✅ Users fetched ONCE');
  } catch (error) {
    console.error('Failed to fetch users:', error);
  } finally {
    setLoading(false);
  }
}, [loading]);

  // Auto-fetch only once on mount
  // useEffect(() => {
  //   if (!initialLoadDone && !fetchCalledRef.current) {
  //     fetchAllUsers();
  //   }
  // }, [fetchAllUsers, initialLoadDone]);

  useEffect(() => {
  fetchAllUsers();
}, []);

  // Get user by ID (returns UserInfo or null)
  const getUser = useCallback((userId: string): UserInfo | null => {
    if (!userId) return null;
    if (currentUser?.id === userId) return currentUser;
    return userMap.get(userId) || null;
  }, [currentUser, userMap]);

  // Get user name by ID
  const getUserName = useCallback((userId: string): string => {
    if (!userId) return 'System';
    if (currentUser?.id === userId) return currentUser.name;
    const user = userMap.get(userId);
    return user?.name || userId.slice(0, 8);
  }, [currentUser, userMap]);

  // Get user role by ID
  const getUserRole = useCallback((userId: string): string => {
    if (!userId) return 'System';
    if (currentUser?.id === userId) return currentUser.role;
    const user = userMap.get(userId);
    return user?.role || 'Employee';
  }, [currentUser, userMap]);

  // Get user email by ID
  const getUserEmail = useCallback((userId: string): string => {
    if (!userId) return '';
    if (currentUser?.id === userId) return currentUser.email;
    const user = userMap.get(userId);
    return user?.email || '';
  }, [currentUser, userMap]);

  // Get all users list (for dropdowns)
  const getAllUsersList = useCallback(() => {
    return allUsersList;
  }, [allUsersList]);

  // Manual refresh function (call this when you need to reload)
  const refresh = useCallback(() => {
    fetchCalledRef.current = false;
    setInitialLoadDone(false);
    fetchAllUsers();
  }, [fetchAllUsers]);

  return {
    currentUser,
    loading,
    allUsers: allUsersList,
    getUser,
    getUserName,
    getUserRole,
    getUserEmail,
    getAllUsersList,
    refresh,
  };
}